// Lib
import Handsontable from 'handsontable/base';
import { BasePlugin } from 'handsontable/plugins';
import { cloneDeep, isNumber, isString } from 'lodash';

// Utils
import { getFirstSelection, getStartCol, getStartRow } from '../utils/selectionUtils/tableSelectionAccessors';
import { getAllCellsBetween } from '../utils/selectionUtils/tableSelectionGeometryUtils';
import { isSelectingRefColHeader } from '../utils/selectionUtils/tableSelectionPredicateUtils';
import MilanoteEditingCleanupPlugin, { MILANOTE_EDITING_CLEANUP_PLUGIN_NAME } from './MilanoteEditingCleanupPlugin';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';
import { stringShouldFormatAsType } from '../../../../common/table/utils/tableInputGeneralUtils';
import {
    getColumnCount,
    isCellTypeCurrency,
    isCellTypePercentage,
} from '../../../../common/table/utils/tableCellDataPropertyUtils';

// Types
import { STYLE_COMMANDS } from '../../../components/editor/richText/richTextConstants';
import { CellCoordsObj } from '../../../../common/table/TableTypes';
import {
    addHotTableColsLeft,
    addHotTableColsRight,
    addHotTableRowsAbove,
    addHotTableRowsBelow,
    removeHotTableCols,
    removeHotTableRows,
} from '../utils/hotInstanceUtils/grid/hotTableGridOperationsUtils';
import { updateHotTableSelectedCellTextStyle } from '../utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';

export const MILANOTE_EDITING_PLUGIN_NAME = 'milanoteEditing';
// TODO-TABLES - When the legacy architecture is removed, set this priority to 1
//  (cannot have two plugins with same priority)
export const MILANOTE_EDITING_PLUGIN_PRIORITY = 2;
const SHORTCUTS_GROUP_MILANOTE_CELL_FORMATTING = 'MilanoteEditingPlugin.formatting';
const SHORTCUTS_GROUP_MILANOTE_GRID_UPDATE = 'MilanoteEditingPlugin.gridUpdate';
/**
 * MilanoteEditingPlugin.ts
 *
 *   - This is a custom plugin that has a high priority (called before all other plugins) used for Milanote table editing setup
 *
 *   - Normalise the Handsontable cell values into values that is inferrable by HyperFormula.
 *
 *      Process:
 *      afterSetDataAtCell hook -> MilanoteEditingPlugin -> FormulaPlugin -> MilanoteEditingCleanupPlugin
 *      For diagram, see https://app.milanote.com/1Qjnx50VbE5g69/table-editing-flow
 *
 *   - Sets appropriate shortcuts for formatting and grid update
 */
class MilanoteEditingPlugin extends BasePlugin {
    /**
     * This key will be used as a prop key when enabling this plugin in the HotTable component
     */
    static get PLUGIN_KEY(): string {
        return MILANOTE_EDITING_PLUGIN_NAME;
    }

    static get PLUGIN_PRIORITY(): number {
        return MILANOTE_EDITING_PLUGIN_PRIORITY;
    }

    getPluginSettings(): {
        jumpToTitle: () => void;
        isReadOnly: boolean;
    } {
        const settings = this.hot?.getSettings() as any;
        return settings?.[MILANOTE_EDITING_PLUGIN_NAME];
    }

    getConfig(): {
        jumpToTitle: () => void;
        isReadOnly: boolean;
    } {
        return this.getPluginSettings() ?? {};
    }

    /**
     * This function is needed to determine whether or not the plugin is enabled. In the case of this plugin, always
     * enable it.
     */
    isEnabled(): boolean {
        return !!this.getPluginSettings();
    }

    /**
     * The `enablePlugin` method is triggered on the `beforeInit` hook.
     * It should contain the plugin's initial setup and hook connections.
     * This method is run only if the `isEnabled` method returns `true`.
     */
    enablePlugin(): void {
        this.addHook('afterInit', (...args) => this.afterInit(...args));
        this.addHook('afterSetDataAtCell', (...args) => this.onAfterSetDataAtCell(...args));
        this.addHook('afterUnlisten', () => this.onAfterUnlisten());
        this.addHook('beforeOnCellMouseDown', (event) => this.onBeforeOnCellMouseDown(event));
        this.addHook('beforeInitWalkontable', (config) => this.beforeInitWalkontable(config));

        // The `super` method sets the `this.enabled` property to `true`.
        // It is a necessary step to update the plugin's settings properly.
        super.enablePlugin();
    }

    disablePlugin(): void {
        this.unregisterShortcuts();
    }

    onBeforeOnCellMouseDown = (event: MouseEvent): void => {
        // If the table is readonly, prevent all mouse events from propagating
        if (this.getConfig().isReadOnly) event.stopImmediatePropagation();
    };

    /**
     * The Formula plugin listens to the `afterSetDataAtCell` hook to update the formula cells.
     *
     * Since Milanote table has its own implementation of cell types and cell formatting,
     * which is external to Handsontable and Hyperformula. Before we pass in values to Hyperformula,
     * we need to update the values first so that Hyperformula would be able to infer the HF cell type.
     *
     * This `onAfterSetDataAtCell` function:
     *  - Saves the original changes in `MilanoteEditingCleanupPlugin`, for restoration later after all the other
     *    plugins have run
     *  - Convert the changes to a readable format for the formula plugin
     *
     * The changes will be returned to its original format in `MilanoteEditingCleanupPlugin`
     */
    onAfterSetDataAtCell(changes: Handsontable.CellChange[], source?: Handsontable.ChangeSource): void {
        const milanoteEditingCleanupPlugin = this.hot.getPlugin(
            MILANOTE_EDITING_CLEANUP_PLUGIN_NAME,
        ) as MilanoteEditingCleanupPlugin;

        milanoteEditingCleanupPlugin.setOriginalChanges(cloneDeep(changes));

        // These changes will be saved to Hyperformula, and get converted back to the original format in
        // `MilanoteEditingCleanupPlugin`
        changes.forEach(([row, col, prevValue, newValue], index) => {
            if (!isNumber(col)) return;

            const prevHFValue = this.normaliseCellValueForHyperFormula(prevValue, { row, col }, source);
            const newHFValue = this.normaliseCellValueForHyperFormula(newValue, { row, col }, source);

            changes[index] = [row, col, prevHFValue, newHFValue];
        });
    }

    onAfterUnlisten(): void {
        // This is to solve an issue where undoing a table element creation (which removes the table element) will
        // persist focus on textarea.HandsontableCopyPaste, which will cause board level shortcuts to not work
        document.querySelector<HTMLTextAreaElement>('textarea.HandsontableCopyPaste')?.blur();
    }

    afterInit(): void {
        this.registerShortcuts();

        // @ts-ignore - valid property
        const mouseDownEvent = this.hot.eventListeners?.find(
            // @ts-ignore - valid property
            ({ event, element }) => event === 'mousedown' && element.nodeName === 'HTML',
        );

        if (mouseDownEvent) {
            const { eventManager, element, event: eventName, callback } = mouseDownEvent;

            eventManager.removeEventListener(element, eventName, callback);
            eventManager.addEventListener(element, eventName, (event: MouseEvent) => {
                let next = event.target as HTMLElement | null;

                while (next && next !== element) {
                    if (next.hasAttribute('data-table-util')) return;

                    next = next.parentElement;
                }

                callback(event);
            });
        }

        // Prevent Handsontable from scrolling Milanote's viewport

        // @ts-ignore - valid property
        this.hot.view.scrollViewport = () => {
            // Do nothing
        };

        // @ts-ignore - valid property
        this.hot.view.scrollViewportHorizontally = () => {
            // Do nothing
        };

        // @ts-ignore - valid property
        this.hot.view.scrollViewportVertically = () => {
            // Do nothing
        };
    }

    /**
     * Disable the default onContainerElementResize, as it will cause the table to rerender on Handsontable's side.
     * This is not applicable to Milanote's tables, as the container element's size will have limited effect on the
     * content of the table.
     */
    beforeInitWalkontable(config: any): void {
        config.onContainerElementResize = null;
    }

    /********************
     * CELL TEXT FORMATTING
     ********************/

    /**
     * Normalise the Handsontable cell value into the plain value that HyperFormula expects.
     */
    normaliseCellValueForHyperFormula = (
        cellValue: string | number | null,
        cellCoords: CellCoordsObj,
        source?: string,
    ): string | number => {
        // @ts-ignore - custom property
        const { locale } = this.hot?.milanoteProps || {};
        const { cellData } = this.hot.getCellMeta(cellCoords.row, cellCoords.col) || {};

        let newHFValue = cellValue;

        // Formatting numbers here so that english number formatting will be saved to Hyperformula
        if (isString(cellValue)) {
            newHFValue = stringShouldFormatAsType(cellValue, true, locale)?.newCellValue || cellValue;
        }

        if (newHFValue !== null && !isFormula(cellData?.value)) {
            // If currency, prefix a $ to the value given to Hyperformula, so that it can read it as a currency
            if (isCellTypeCurrency(cellData)) {
                newHFValue = `$${newHFValue}`;
            }

            // If percentage, convert to `x%` format so that Hyperformula can read it as a percentage
            if (isCellTypePercentage(cellData) && isNumber(newHFValue)) {
                newHFValue = `${newHFValue * 100}%`;
            }

            if (isCellTypePercentage(cellData) && isString(newHFValue) && !newHFValue.endsWith('%')) {
                newHFValue = `${parseInt(newHFValue) * 100}%`;
            }
        }

        // WEB-14238 - For now, empty cells will fallback to ''. This is to prevent a bug where Hyperformula does not
        // calculate the table dimensions correctly if table contains null cells, causing an error to appear.
        //
        // This also means that functions like COUNTBLANK() and COUNTIF() will not be handled properly.
        return newHFValue ?? '';
    };

    /********************
     * SHORTCUTS
     ********************/

    registerShortcuts(): void {
        const shortcutManager = this.hot.getShortcutManager();

        const gridContext = shortcutManager.getContext('grid');

        if (!gridContext) return;

        // Remove Handsontable default shortcut (Ctrl/Cmd + Enter) to avoid conflict with Milanote's shortcut
        gridContext.removeShortcutsByKeys(['Control/Meta', 'Enter']);
        gridContext.removeShortcutsByKeys(['Backspace']);
        gridContext.removeShortcutsByKeys(['Delete']);

        const { jumpToTitle } = this.getConfig();

        gridContext.addShortcuts(
            [
                {
                    keys: [['Control/Meta', 'b']],
                    callback: () => updateHotTableSelectedCellTextStyle(this.hot, STYLE_COMMANDS.BOLD),
                },
                {
                    keys: [['Control/Meta', 'i']],
                    callback: () => updateHotTableSelectedCellTextStyle(this.hot, STYLE_COMMANDS.ITALIC),
                },
                {
                    keys: [['Control/Meta', 'shift', 'x']],
                    callback: () => updateHotTableSelectedCellTextStyle(this.hot, STYLE_COMMANDS.STRIKETHROUGH),
                },
            ],
            // @ts-ignore - Second argument is not documented in Handsontable TS
            { group: SHORTCUTS_GROUP_MILANOTE_CELL_FORMATTING },
        );

        gridContext.addShortcuts(
            [
                {
                    keys: [['Control/Meta', 'Enter']],
                    callback: (event) => {
                        event.preventDefault();
                        event.stopImmediatePropagation();

                        const cellSelections = this.hot.getSelected();
                        if (!cellSelections) return;

                        const firstCellSelection = getFirstSelection(cellSelections);
                        if (firstCellSelection && isSelectingRefColHeader(firstCellSelection)) {
                            addHotTableColsRight(this.hot, undefined);
                            return;
                        }

                        addHotTableRowsBelow(this.hot, undefined);
                    },
                },
                {
                    keys: [['Control/Meta', 'Backspace']],
                    callback: (event) => {
                        event.preventDefault();
                        event.stopImmediatePropagation();

                        const cellSelections = this.hot.getSelected();
                        if (!cellSelections) return;

                        const firstCellSelection = getFirstSelection(cellSelections);
                        if (firstCellSelection && isSelectingRefColHeader(firstCellSelection)) {
                            removeHotTableCols(this.hot, undefined);
                            return;
                        }

                        removeHotTableRows(this.hot, undefined);
                    },
                },
                {
                    keys: [['Tab']],
                    callback: (event) => {
                        const cellSelections = this.hot.getSelected();
                        if (!cellSelections) return;

                        const nCols = getColumnCount(this.hot.getData());
                        const firstCellSelection = getFirstSelection(cellSelections);

                        if (!firstCellSelection) return;

                        const isSelectingLastColumn = getStartCol(firstCellSelection) === nCols - 1;
                        if (isSelectingLastColumn) {
                            addHotTableColsRight(this.hot, undefined);
                        }
                    },
                },
                {
                    keys: [['Shift', 'Tab']],
                    callback: (event) => {
                        const cellSelections = this.hot.getSelected();
                        if (!cellSelections) return;

                        const firstCellSelection = getFirstSelection(cellSelections);

                        if (!firstCellSelection) return;

                        const isSelectingFirstCell =
                            getStartRow(firstCellSelection) === 0 && getStartCol(firstCellSelection) === 0;

                        if (isSelectingFirstCell) jumpToTitle();
                    },
                },
                {
                    keys: [
                        ['Backspace'],
                        ['Delete'],

                        // This is to cater to iPad, where shift key is by default enabled when the keyboard shows up
                        // to capitalise the first letter of the input.
                        ['Shift', 'Backspace'],
                    ],
                    callback: () => {
                        const cellSelections = this.hot.getSelected();
                        if (!cellSelections) return;

                        // Revert readOnly cells to be editable so that they can be cleared
                        getAllCellsBetween(cellSelections).forEach(({ row, col }) => {
                            this.hot.setCellMetaObject(row, col, { readOnly: false });
                        });

                        this.hot.emptySelectedCells();

                        // Re-render after emptying cells to ensure table headers gets properly resized
                        requestAnimationFrame(() => this.hot && !this.hot.isDestroyed && this.hot.render());
                    },
                },
                {
                    keys: [['Alt', 'ArrowUp']],
                    callback: () => {
                        addHotTableRowsAbove(this.hot, undefined);
                    },
                },
                {
                    keys: [['Alt', 'ArrowDown']],
                    callback: () => {
                        addHotTableRowsBelow(this.hot, undefined);
                    },
                },
                {
                    keys: [['Alt', 'ArrowLeft']],
                    callback: () => {
                        addHotTableColsLeft(this.hot, undefined);
                    },
                },
                {
                    keys: [['Alt', 'ArrowRight']],
                    callback: () => {
                        addHotTableColsRight(this.hot, undefined);
                    },
                },
                {
                    keys: [
                        ['ArrowUp'],
                        ['ArrowRight'],
                        ['ArrowDown'],
                        ['ArrowLeft'],
                        ['Shift', 'ArrowUp'],
                        ['Shift', 'ArrowRight'],
                        ['Shift', 'ArrowDown'],
                        ['Shift', 'ArrowLeft'],
                    ],
                    callback: () => {
                        // Do nothing. This is to prevent the element from shifting around on canvas when using
                        // arrow keys to change cell selection.
                    },
                    runOnlyIf: () => this.hot.getSelected() !== undefined,
                    stopPropagation: true,
                },
            ],
            // @ts-ignore - Second argument is not documented in Handsontable TS
            { group: SHORTCUTS_GROUP_MILANOTE_GRID_UPDATE, position: 'before', relativeToGroup: 'gridDefault' },
        );
    }

    unregisterShortcuts(): void {
        const shortcutManager = this.hot.getShortcutManager();
        const gridContext = shortcutManager.getContext('grid');

        if (!gridContext) return;

        gridContext.removeShortcutsByGroup(SHORTCUTS_GROUP_MILANOTE_CELL_FORMATTING);
        gridContext.removeShortcutsByGroup(SHORTCUTS_GROUP_MILANOTE_GRID_UPDATE);
    }
}

export default MilanoteEditingPlugin;
